import { useState, useEffect } from "react";
import { ArrowLeft, Users, Mail, Calendar, Loader2, CreditCard, Shield, ShieldCheck, ShieldX, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getAdminUsersStripe, toggleManualAccess } from "@/lib/api";
import { toast } from "sonner";

export default function AdminUsers({ onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const fetchUsers = () => {
    setLoading(true);
    getAdminUsersStripe()
      .then(res => setUsers(res.data))
      .catch(() => toast.error("Erreur chargement utilisateurs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleToggleAccess = async (userId, currentAccess) => {
    setTogglingId(userId);
    try {
      const res = await toggleManualAccess(userId, !currentAccess);
      setUsers(prev => prev.map(u =>
        u.user_id === userId ? { ...u, manual_access: !currentAccess } : u
      ));
      toast.success(!currentAccess ? "Accès manuel activé" : "Accès manuel désactivé");
      const d = res?.data;
      if (d?.email_status === "sent") toast.success("E-mail envoyé", { description: `L'utilisateur a été prévenu par e-mail (${d.email_to}).` });
      else if (d?.email_status === "failed") toast.error("E-mail non envoyé", { description: "L'accès est bien activé, mais l'e-mail n'a pas pu partir." });
    } catch {
      toast.error("Erreur lors de la modification");
    }
    setTogglingId(null);
  };

  const getStatusBadge = (u) => {
    if (u.email === "bouranepro@gmail.com") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: "#2D6A4F20", color: "#2D6A4F" }}>
          <Shield className="w-3 h-3" /> Admin
        </span>
      );
    }
    if (u.manual_access) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: "#3B82F620", color: "#3B82F6" }}>
          <ShieldCheck className="w-3 h-3" /> Accès manuel
        </span>
      );
    }
    if (u.subscription_status === "active") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: "#10B98120", color: "#10B981" }}>
          <CreditCard className="w-3 h-3" /> Abonné
        </span>
      );
    }
    if (u.subscription_status === "payment_failed") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: "#EF444420", color: "#EF4444" }}>
          <ShieldX className="w-3 h-3" /> Paiement échoué
        </span>
      );
    }
    if (u.subscription_status === "expired") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
          style={{ background: "#F5920020", color: "#F59200" }}>
          <ShieldX className="w-3 h-3" /> Expiré
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
        style={{ background: "#6B728020", color: "#6B7280" }}>
        Inactif
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white" data-testid="admin-users-panel">
      {/* Header */}
      <div className="p-4 border-b border-black/5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} data-testid="back-from-admin-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1A1A' }}>
              Gestion des comptes
            </h2>
            <p className="text-[11px]" style={{ color: '#4A4A4A' }}>
              {users.length} utilisateur{users.length > 1 ? 's' : ''} — Abonnements et accès
            </p>
          </div>
        </div>
      </div>

      {/* User list */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#2D6A4F' }} />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-8 h-8 mx-auto mb-3" style={{ color: '#4A4A4A' }} />
            <p className="text-sm" style={{ color: '#4A4A4A' }}>Aucun utilisateur</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {users.map((u, i) => (
              <div
                key={u.user_id || i}
                className="p-3 rounded-xl border border-black/5 hover:border-black/10 transition-colors space-y-2"
                data-testid={`user-item-${i}`}
              >
                {/* User info row */}
                <div className="flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name || u.email}
                      className="w-10 h-10 rounded-full object-cover border border-black/5" />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ background: '#2D6A4F15', color: '#2D6A4F' }}>
                      {(u.name || u.email || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate" style={{ color: '#1A1A1A' }}>
                        {u.name || "Sans nom"}
                      </p>
                      {getStatusBadge(u)}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Mail className="w-3 h-3 shrink-0" style={{ color: '#7A7A7A' }} />
                      <p className="text-[11px] truncate" style={{ color: '#4A4A4A' }}>{u.email}</p>
                    </div>
                  </div>
                </div>

                {/* Subscription details */}
                <div className="flex items-center justify-between pl-[52px]">
                  <div className="space-y-0.5">
                    {u.subscription_expires && u.subscription_status === "active" && (
                      <p className="text-[10px]" style={{ color: '#7A7A7A' }}>
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Expire le {new Date(u.subscription_expires).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                    {u.latest_payment && (
                      <p className="text-[10px]" style={{ color: '#7A7A7A' }}>
                        <CreditCard className="w-3 h-3 inline mr-1" />
                        Dernier paiement: {u.latest_payment.payment_status} — {u.latest_payment.amount}€
                      </p>
                    )}
                  </div>

                  {/* Toggle manual access (not for admin) */}
                  {u.email !== "bouranepro@gmail.com" && (
                    <button
                      onClick={() => handleToggleAccess(u.user_id, u.manual_access)}
                      disabled={togglingId === u.user_id}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors hover:bg-black/5"
                      style={{ color: u.manual_access ? '#2D6A4F' : '#7A7A7A' }}
                      data-testid={`toggle-access-${u.user_id}`}
                    >
                      {togglingId === u.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : u.manual_access ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                      {u.manual_access ? "Accès ON" : "Accès OFF"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
